import { AxiosResponse } from 'axios';
import { parse as parseContentDisposition } from 'content-disposition';
import { saveAs } from 'file-saver';
import mime from 'mime';

/**
 * Parses the content-disposition header to extract filename using the content-disposition library
 */
function parseFilename(contentDisposition: string | undefined): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  try {
    const parsed = parseContentDisposition(contentDisposition);
    return parsed.parameters?.filename;
  } catch (error) {
    // If parsing fails, return undefined
    return undefined;
  }
}

/**
 * Gets file extension from filename or MIME type
 */
function getFileExtension(filename: string | undefined, contentType: string | undefined): string {
  // If filename has extension, extract it
  if (filename) {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot !== -1) {
      return filename.substring(lastDot);
    }
  }

  // Otherwise, try to get extension from MIME type
  if (contentType) {
    // Remove any charset or other parameters (e.g., "text/csv; charset=utf-8" -> "text/csv")
    const mimeType = contentType.split(';')[0].trim().toLowerCase();
    const extension = mime.getExtension(mimeType);
    return extension ? `.${extension}` : '';
  }

  return '';
}

/**
 * Saves a file from an axios response by automatically detecting format, MIME type, and filename from headers
 * @param response - The axios response containing the file data and headers
 */
export function saveFileResponse(response: AxiosResponse<any>): void {
  const { data, headers } = response;

  if (!data) {
    return;
  }

  // Extract headers (axios normalizes headers to lowercase)
  const contentType = headers['content-type'];
  const contentDisposition = headers['content-disposition'];

  // Parse filename from content-disposition
  const filename = parseFilename(contentDisposition);

  // Get file extension
  const extension = getFileExtension(filename, contentType);

  // Determine final filename
  let finalFilename: string;
  if (filename) {
    finalFilename = filename;
  } else {
    // Generate a filename with the correct extension
    const timestamp = Date.now();
    finalFilename = `download-${timestamp}${extension}`;
  }

  // Determine MIME type for blob
  let blobType: string;
  if (contentType) {
    // Remove any charset or other parameters
    blobType = contentType.split(';')[0].trim();
  } else {
    // Fallback: try to infer from extension
    const ext = extension.toLowerCase().replace(/^\./, ''); // Remove leading dot if present
    const mimeType = mime.getType(ext);
    blobType = mimeType || 'application/octet-stream';
  }

  // Create blob and download
  const blob = new Blob([data], { type: blobType });
  saveAs(blob, finalFilename);
}
